import { mountCms } from './admin-cms';
import { esc } from '../admin-ui';

/**
 * CMS as one section (2026-10-14). FAQ, News, Legal and Social used to be four
 * sidebar entries, which made the sidebar mostly CMS and hid the fact that all
 * four feed the same public pages. One entry now, with the four as tabs.
 */

export const CMS_TABS = [
  { id: 'faq', label: 'FAQ' },
  { id: 'news', label: 'News' },
  { id: 'legal', label: 'Legal' },
  { id: 'social', label: 'Social' },
] as const;

export type CmsTab = (typeof CMS_TABS)[number]['id'];

/** Survives leaving the section and coming back within one visit. */
let lastTab: CmsTab = 'faq';

export async function mountCmsHub(container: HTMLElement, initial?: CmsTab): Promise<() => void> {
  let tab: CmsTab = initial ?? lastTab;
  let teardown: (() => void) | null = null;
  let seq = 0;

  container.innerHTML = `
    <h2 class="font-heading text-gold text-lg mb-3">CMS</h2>
    <div role="tablist" aria-label="Content" class="flex flex-wrap gap-1.5 mb-4 border-b border-border pb-2">
      ${CMS_TABS.map((t) => `
        <button type="button" role="tab" id="adm-cms-tab-${t.id}" data-cms-tab="${t.id}"
          aria-controls="adm-cms-body"
          class="min-h-11 px-4 rounded text-[13px] transition-colors">${esc(t.label)}</button>`).join('')}
    </div>
    <div id="adm-cms-body" role="tabpanel"></div>`;

  const body = container.querySelector<HTMLElement>('#adm-cms-body')!;
  const tabs = [...container.querySelectorAll<HTMLButtonElement>('[data-cms-tab]')];

  async function show(next: CmsTab): Promise<void> {
    tab = next;
    lastTab = next;
    const mine = ++seq;
    for (const b of tabs) {
      const on = b.dataset.cmsTab === next;
      b.setAttribute('aria-selected', String(on));
      b.tabIndex = on ? 0 : -1;
      b.classList.toggle('bg-gold/15', on);
      b.classList.toggle('text-gold', on);
      b.classList.toggle('font-semibold', on);
      b.classList.toggle('text-text', !on);
    }
    body.setAttribute('aria-labelledby', `adm-cms-tab-${next}`);
    teardown?.();
    teardown = null;
    body.innerHTML = '<p class="text-muted text-center py-8">Loading&hellip;</p>';
    const t = await mountCms(body, next);
    // A quick second click can finish before the first; keep only the latest.
    if (mine === seq) teardown = t; else t();
  }

  tabs.forEach((b) => b.addEventListener('click', () => {
    const next = b.dataset.cmsTab as CmsTab;
    if (next !== tab || !teardown) void show(next);
  }));

  container.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
    const ev = e as KeyboardEvent;
    const dir = ev.key === 'ArrowRight' ? 1 : ev.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    ev.preventDefault();
    const at = CMS_TABS.findIndex((t) => t.id === tab);
    const next = CMS_TABS[(at + dir + CMS_TABS.length) % CMS_TABS.length].id;
    void show(next).then(() => container.querySelector<HTMLButtonElement>(`[data-cms-tab="${next}"]`)?.focus());
  });

  await show(tab);
  return () => { seq++; teardown?.(); teardown = null; };
}
